import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SingleOrArray } from '../../../../models/defines';
import { IWSContext } from '../../../../models/workspace/workspace-model';
import WorkspaceAssetsUrls from '../../../../constants/private/urls/workspace/associations/assets/workspace-assets.urls';
import {
    IGenerateImageRequest,
    IGenerateVideoRequest,
    IMoveAssets,
    IRenameAsset,
} from '../../../http-models/asset/asset-http-request-model';
import WorkspaceAssetsDetailsUrls from '../../../../constants/private/urls/workspace/associations/assets/workspace-assets-details.urls';
import { IExternalLibraryUpload } from '../../../shared/utils/asset-utils.service';
import { IAssetInDTO, ILocalAsset } from '../../../../models/assets/assets.model';
import {
    IAssetFolderDeleteCommand,
    IAssetsResponse,
    IGetAssetsParams,
    IProjectAssetMembershipCommand,
} from './workspace-asset-request.types';

@Injectable()
export class WorkspaceAssetApiService {
    constructor(private http: HttpClient) {}

    upload$ = (formData: FormData, wsContext: IWSContext, extraParams = {}) => {
        const url = WorkspaceAssetsUrls.getUploadAssetPOSTUrl(wsContext);
        let httpParams = new HttpParams();

        // Add extra query parameters dynamically
        Object.keys(extraParams).forEach((key) => {
            httpParams = httpParams.set(key, extraParams[key]);
        });
        return this.http.post(url, formData, {
            reportProgress: true,
            observe: 'events',
            params: httpParams,
        });
    };

    createFolder$(wsContext: IWSContext, folderName: string) {
        const url = WorkspaceAssetsUrls.getCreateFolderPOSTUrl(wsContext);
        return this.http.post<IAssetInDTO>(url, { newName: folderName });
    }
    renameFileAsset$(wsContext: IWSContext, data: IRenameAsset) {
        const url = WorkspaceAssetsDetailsUrls.getUpdateFileNamePUTUrl(wsContext, data.assetId);
        return this.http.put<IAssetInDTO>(url, { newName: data.newName });
    }
    moveAssetToFolder$(wsContext: IWSContext, moveAssetPayload: IMoveAssets) {
        const url = WorkspaceAssetsUrls.getMoveFilePUTUrl(wsContext);
        return this.http.put<IAssetInDTO>(url, moveAssetPayload);
    }
    getAssetsInWorkspace$(wsContext: IWSContext, params: IGetAssetsParams = {}) {
        const url = WorkspaceAssetsUrls.getAssetsListGETUrl(wsContext);
        let httpParams = new HttpParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                httpParams = httpParams.set(key, String(value));
            }
        });
        return this.http.get<IAssetsResponse>(url, { params: httpParams });
    }
    getAssetsByIdsAsync$(wsContext: IWSContext, assetIds: SingleOrArray<string>) {
        const url = WorkspaceAssetsUrls.getSpecificAssetByIdsGETUrl(wsContext);

        const ids = Array.isArray(assetIds) ? assetIds : [assetIds];

        let params = new HttpParams();
        ids.forEach((assetId) => {
            params = params.append('assetIds', assetId);
        });

        return this.http.get<IAssetInDTO[]>(url, { params });
    }

    removeAssetsFromWS$(wsContext: IWSContext, assetIds: SingleOrArray<string>) {
        return this.trashAssets$(wsContext, assetIds);
    }

    trashAssets$(wsContext: IWSContext, assetIds: SingleOrArray<string>) {
        return this.http.delete<void>(WorkspaceAssetsUrls.getRemoveAssetsDELETEUrl(wsContext), {
            body: {
                assetIds: assetIds,
            },
        });
    }

    restoreAssets$(wsContext: IWSContext, assetIds: SingleOrArray<string>) {
        const url = `${WorkspaceAssetsUrls.getWorkspaceAssetsUrl(wsContext)}/restore`;
        return this.http.post<void>(url, { assetIds });
    }

    permanentlyRemoveAssets$(wsContext: IWSContext, assetIds: SingleOrArray<string>) {
        const url = `${WorkspaceAssetsUrls.getWorkspaceAssetsUrl(wsContext)}/permanent`;
        return this.http.delete<void>(url, { body: { assetIds } });
    }

    addAssetsToProject$(wsContext: IWSContext, command: IProjectAssetMembershipCommand) {
        const url = `${WorkspaceAssetsUrls.getWorkspaceAssetsUrl(wsContext)}/project-membership`;
        return this.http.post<void>(url, command, { observe: 'response' });
    }

    removeAssetsFromProject$(wsContext: IWSContext, command: IProjectAssetMembershipCommand) {
        const url = `${WorkspaceAssetsUrls.getWorkspaceAssetsUrl(wsContext)}/project-membership`;
        return this.http.delete<{ removedAssetIds?: string[]; blocked?: Array<{ assetId: string; usages: unknown[] }> }>(url, { body: command });
    }

    renameFolder$(wsContext: IWSContext, assetId: string, newName: string) {
        const url = `${WorkspaceAssetsUrls.getWorkspaceAssetsUrl(wsContext)}/folder/${assetId}`;
        return this.http.put<IAssetInDTO>(url, { newName });
    }

    deleteFolder$(wsContext: IWSContext, assetId: string, command: IAssetFolderDeleteCommand) {
        const url = `${WorkspaceAssetsUrls.getWorkspaceAssetsUrl(wsContext)}/folder/${assetId}`;
        return this.http.delete<void>(url, { params: new HttpParams().set('children', command.children) });
    }

    uploadPhotoFromPhotoLibrary$(wsContext: IWSContext, body: IExternalLibraryUpload, toFolder: string) {
        const url = WorkspaceAssetsUrls.getUploadPhotoFromPhotoLibrary(wsContext);
        return this.http.post<string>(url, { ...body, toFolder });
    }

    generateVideo$(wsContext: IWSContext, body: IGenerateVideoRequest) {
        const url = WorkspaceAssetsUrls.getGenerateVideoPOSTUrl(wsContext);
        return this.http.post<{ success: true }>(url, body);
    }

    generateImage$(wsContext: IWSContext, body: IGenerateImageRequest) {
        const url = WorkspaceAssetsUrls.getGenerateImagePOSTUrl(wsContext);
        return this.http.post<{ success: true; uploadPath: string; assetId: string; asset: ILocalAsset }>(url, body);
    }
}
